import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { UserCog } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { PageLayout } from '../../../components/ui/PageLayout';
import { LoadingState } from '../../../components/AsyncStates';
import { cn } from '@/utils/cn';
import RefereeListPanel from './components/RefereeListPanel';
import MatchDetailsPanel from './components/MatchDetailsPanel';
import AssignmentActionPanel from './components/AssignmentActionPanel';
import ConfirmAssignmentModal from './components/ConfirmAssignmentModal';
import { useAllReferees } from '@/hooks/useReferees';
import { useAllMatches } from '@/hooks/useMatches';
import { useRefereeRecommendations } from '@/hooks/useRefereeRecommendations';
import { useDebounce } from '@/hooks/useDebounce';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import { getReferee } from '@/api/TournamentManagement/refereeApi';
import { RefereeCertification, resolveEnum } from '@/models/tournament';
import { Roles } from '@/models/general';
import type { RootState } from '@/store/redux/store/store';
import { isClosedMatchStatus, isPlayedMatchStatus } from '@/utils/tournamentPostCompletion';
import type {
  RefereeView,
  MatchView,
} from '@/models/tournament';

const RefereeAssignmentCenter: React.FC = () => {
  const { t } = useTranslation('tournament');
  const { toast, showToast, dismissToast } = useToast();
  // The selected match is derived from the list, not copied out of it: after an assignment the list
  // is refetched and the panel/modal must show the NEW roster, not the snapshot taken on click.
  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
  const [selectedReferee, setSelectedReferee] = useState<RefereeView | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [certificationFilter, setCertificationFilter] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [assignmentType, setAssignmentType] = useState<'new' | 'reassignment'>('new');

  // The search box fed the server query on EVERY keystroke, and the page unmounted all three panels
  // while that query was in flight — so typing blanked the whole screen letter by letter and the
  // selected match's details flashed away underneath the cursor. Debounced, same as
  // referee-management.
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Referee query
  // The whole roster, not one page. `pageSize: 50` here meant a tenant with more referees than
  // that could not assign the rest — they never rendered in the panel and nothing said why.
  const refereeQuery = useMemo(() => ({
    search: debouncedSearch || undefined,
    certification: certificationFilter !== '' ? (parseInt(certificationFilter) as RefereeCertification) : undefined,
  }), [debouncedSearch, certificationFilter]);

  const {
    data: refereeData,
    loading: refereesLoading,
    error: refereesError,
    refetch: refetchReferees,
  } = useAllReferees(refereeQuery);

  // Match query - load matches that need assignment (Scheduled or InProgress)
  const matchQuery = useMemo(() => ({}), []);

  const {
    data: matchData,
    loading: matchesLoading,
    error: matchesError,
    refetch: refetchMatches,
  } = useAllMatches(matchQuery);

  // Both hooks flip `loading` on every refetch — filter change, assignment, search keystroke — and
  // the page used to unmount ALL THREE panels for the duration. Spinner only when there is nothing
  // to show yet; afterwards the panels stay mounted and dim.
  const isInitialLoad = (refereesLoading && !refereeData) || (matchesLoading && !matchData);
  const isRefetching = !isInitialLoad && (refereesLoading || matchesLoading);

  const matches = useMemo(() => matchData?.items ?? [], [matchData]);
  const selectedMatch = useMemo(
    () => matches.find((m) => m.id === selectedMatchId) ?? null,
    [matches, selectedMatchId],
  );

  // CO-921 (owner 2026-10-21): changing who officiated a PLAYED match moves its work points and pay,
  // so only HR may do it (the server answers Admin with 403 TOURNAMENT_POSTCOMPLETION_HR_ONLY), and a
  // Closed match changes only after Reopen. Say why up front instead of letting the dialog dead-end.
  const userRole = useSelector((s: RootState) => s.user.role) as string | undefined;
  const isHr = userRole === Roles.Hr;
  const assignBlockedReason: string | null = !selectedMatch
    ? null
    : isClosedMatchStatus(selectedMatch.status)
      ? t('wpFollow.closedReopenFirst')
      : isPlayedMatchStatus(selectedMatch.status) && !isHr
        ? t('wpFollow.hrOnlyChange')
        : null;

  // Fetch recommendations for the selected match
  const { data: recommendations, loading: recommendationsLoading } = useRefereeRecommendations(
    selectedMatchId
  );

  const certificationKeys: Record<RefereeCertification, string> = {
    [RefereeCertification.NationalLevelA]: 'National Level A',
    [RefereeCertification.AFCElite]: 'AFC Elite',
    [RefereeCertification.FIFAInternational]: 'FIFA International',
  };

  const referees = refereeData?.items ?? [];
  const totalReferees = refereeData?.totalCount ?? 0;
  const availableCount = refereeData?.availableCount ?? 0;

  const handleRefereeSelect = (referee: RefereeView) => {
    setSelectedReferee(referee);
  };

  const handleMatchSelect = (match: MatchView) => {
    setSelectedMatchId(match.id);
    setSelectedReferee(null);
  };

  const handleAssignReferee = () => {
    if (!selectedMatch || !selectedReferee || assignBlockedReason) return;

    // Determine assignment type
    if (selectedMatch.refereeTeamMembers?.length > 0) {
      setAssignmentType('reassignment');
    } else {
      setAssignmentType('new');
    }

    setShowConfirmModal(true);
  };

  // A failed lookup used to go to console.error only, so the Quick Assign button visibly did
  // nothing when the server refused. Surfaced through the module's toast convention.
  const handleQuickAssign = async (refereeProfileId: number) => {
    try {
      if (assignBlockedReason) {
        showToast(assignBlockedReason, 'error');
        return;
      }
      const result = await getReferee(refereeProfileId);
      if (result.errors.length === 0 && result.payload) {
        setSelectedReferee(result.payload);
        setAssignmentType(selectedMatch?.refereeTeamMembers?.length ? 'reassignment' : 'new');
        setShowConfirmModal(true);
      } else {
        showToast(result.errors[0]?.description || t('Failed to load official details'), 'error');
      }
    } catch (err: any) {
      showToast(
        err?.errors?.[0]?.description || err?.message || t('Failed to load official details'),
        'error',
      );
    }
  };

  const handleConfirmAssignment = () => {
    setShowConfirmModal(false);
    setSelectedReferee(null);
    // Matches too, not just referees: the roster the panel and the modal read lives on the match.
    // Without this the next assignment into the same role was decided against a pre-assignment
    // roster — no "replace" option offered, and the server answered 400 (or silently added a
    // second assistant alongside).
    refetchMatches();
    refetchReferees();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCertificationFilter('');
  };

  const hasActiveFilters = !!(searchTerm || certificationFilter);
  const loadError = refereesError || matchesError;

  const retryAll = () => {
    if (refereesError) refetchReferees();
    if (matchesError) refetchMatches();
  };

  return (
    <PageLayout
      title={t('Match Official Assignment')}
      description={t('Manage official assignments with intelligent recommendations and availability tracking')}
      icon={UserCog}
      accentColor="amber"
      maxWidth="1600px"
      stats={[
        { label: t('Total Officials'), value: loadError ? '—' : totalReferees },
        { label: t('Available Today'), value: loadError ? '—' : availableCount, color: 'text-green-600' },
      ]}
      statsColumns={2}
    >
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6" data-testid="referee-filters">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Input
              placeholder={t('Search officials by name, ID, or location...')}
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              data-testid="referee-search-input"
            />
          </div>
          <Select
            value={certificationFilter}
            onChange={setCertificationFilter}
            options={[
              { value: '', label: t('All Certifications') },
              { value: '2', label: t('FIFA International') },
              { value: '1', label: t('AFC Elite') },
              { value: '0', label: t('National Level A') },
            ]}
            data-testid="referee-certification-filter"
          />
        </div>
        {hasActiveFilters && (
          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {t('Showing {{count}} of {{total}} officials', { count: referees.length, total: totalReferees })}
            </p>
            <Button variant="link" size="sm" onClick={clearFilters} data-testid="referee-clear-filters-btn">
              {t('Clear Filters')}
            </Button>
          </div>
        )}
      </div>

      {/* Error State — a failed referee or match fetch used to reach the user as "No referees
          found / Try adjusting your filters" and an empty match box with no message at all. */}
      {loadError && (
        <div
          role="alert"
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-3"
          data-testid="assignment-center-error"
        >
          <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-red-900 dark:text-red-200">
              {refereesError ? t('Error loading officials') : t('Error loading matches')}
            </p>
            <p className="text-sm text-red-700 dark:text-red-300">{loadError}</p>
          </div>
          <Button variant="outline" size="sm" onClick={retryAll} className="ms-auto" data-testid="assignment-center-retry-btn">
            {t('Try Again')}
          </Button>
        </div>
      )}

      {/* Loading State — first load only. */}
      {isInitialLoad && (
        <div className="py-12">
          <LoadingState title={t('Loading data...')} size="md" />
        </div>
      )}

      {/* Three-Panel Layout */}
      {!isInitialLoad && (
        <div
          className={cn(
            'grid grid-cols-12 gap-6',
            'transition-opacity duration-200 ease-out motion-reduce:transition-none',
            isRefetching && 'opacity-60 pointer-events-none',
          )}
          aria-busy={isRefetching}
        >
          {/* Left Panel - Referee List */}
          <div className="col-span-12 lg:col-span-3">
            <RefereeListPanel
              referees={referees}
              selectedReferee={selectedReferee}
              onSelectReferee={handleRefereeSelect}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
            />
          </div>

          {/* Center Panel - Match Details */}
          <div className="col-span-12 lg:col-span-6">
            <MatchDetailsPanel
              matches={matches}
              selectedMatch={selectedMatch}
              onSelectMatch={handleMatchSelect}
            />
          </div>

          {/* Right Panel - Assignment Actions */}
          <div className="col-span-12 lg:col-span-3 space-y-6">
            <AssignmentActionPanel
              selectedMatch={selectedMatch}
              selectedReferee={selectedReferee}
              onAssign={handleAssignReferee}
              blockedReason={assignBlockedReason}
            />

            {/* Best Fit Recommendations */}
            {selectedMatch && ((recommendations && recommendations.length > 0) || recommendationsLoading) && (
              <div className="bg-card border border-border rounded-lg p-4" data-testid="best-fit-recommendations">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="Target" size={20} className="me-2 text-primary" />
                  {t('Best Fit Recommendations')}
                </h3>

                {recommendationsLoading ? (
                  <div className="py-6">
                    <LoadingState title={t('Loading recommendations...')} size="sm" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recommendations?.map((referee, index) => (
                      <div key={referee.refereeProfileId ?? index} className="border border-border rounded-lg p-3 hover:border-primary/50 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' :
                              index === 1 ? 'bg-muted text-muted-foreground' : 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
                            }`}>
                              {referee.rank ?? index + 1}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">{referee.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {t(certificationKeys[(resolveEnum(RefereeCertification, referee.certification) as RefereeCertification)] ?? 'Unknown')}
                              </p>
                            </div>
                          </div>
                          <div className="text-end">
                            <p className="text-xs text-muted-foreground">{t('Fit Score')}</p>
                            <p className="text-lg font-bold text-primary">{referee.fitScore}</p>
                          </div>
                        </div>

                        {/* Estimated Work Points */}
                        {referee.estimatedWorkPoints > 0 && (
                          <div className="mb-2" data-testid={`est-work-points-${referee.refereeProfileId}`}>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800">
                              <Icon name="Zap" size={10} className="me-1" />
                              {t('Est. work points', { points: referee.estimatedWorkPoints })}
                            </span>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-1.5">
                          {referee.fitReasons?.map((reason, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                              <Icon name="CheckCircle" size={10} className="me-1" />
                              {reason}
                            </span>
                          ))}
                        </div>

                        <div className="mt-2 pt-2 border-t border-border">
                          <Button
                            size="sm"
                            fullWidth
                            iconName="UserPlus"
                            iconSize={12}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuickAssign(referee.refereeProfileId);
                            }}
                            data-testid={`quick-assign-btn-${referee.refereeProfileId}`}
                          >
                            {t('Quick Assign')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Below `lg` the three panels stack, so the only "Assign Official" button sits at the very
          bottom of the third one — after two long lists. This docks the action to the bottom of the
          viewport as soon as both selections exist, and disappears at `lg` where the right-hand
          panel is visible alongside the lists. */}
      {!isInitialLoad && selectedMatch && selectedReferee && (
        <div
          className="lg:hidden sticky bottom-0 z-20 mt-6 bg-card border border-border rounded-xl shadow-lg px-4 py-3"
          data-testid="assignment-sticky-action-bar"
        >
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground truncate">
                {selectedMatch.team1Name} {t('vs')} {selectedMatch.team2Name}
              </p>
              <p className="text-sm font-medium text-foreground truncate">{selectedReferee.name}</p>
              {assignBlockedReason && (
                <p id="assign-blocked-reason-sticky" className="text-xs text-muted-foreground">{assignBlockedReason}</p>
              )}
            </div>
            <Button
              onClick={handleAssignReferee}
              iconName="CheckCircle"
              disabled={!!assignBlockedReason}
              aria-describedby={assignBlockedReason ? 'assign-blocked-reason-sticky' : undefined}
              data-testid="assign-referee-sticky-btn"
            >
              {t('Assign Official')}
            </Button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmAssignmentModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmAssignment}
        match={selectedMatch}
        referee={selectedReferee}
        assignmentType={assignmentType}
        isHr={isHr}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default RefereeAssignmentCenter;
